import { fromChartSelectors } from '@mode/charts/domain';
import { AppState } from '@mode/legacy/types-app-state';
import { ChartTypes } from '@mode/shared/contract-common';
import { DataViewResponseTypes } from '@mode/shared/contract-common';
import { FLAMINGO_RESERVED_REFS, generateUUID } from '@mode/shared/util-js';
import { createSelector } from 'reselect';
import { getChartState } from '../lib/chart-helpers';
import { SelectedReportSelectors } from './selected-report.selectors';

// @TODO: (@anthonysimone) [GF] - Once the active-exploration feature is migrated and the facade
// is built out, refactor the direct usage of these selectors out of this file and implement
// the observable creation in the charts.facade
import { fromDatasetResultSelectors, fromSwitchViewSelectors } from '@mode/switch-views/domain';

export namespace ActiveExplorationSelectors {
  export const getActiveExplorationToken = (state: AppState) => state.activeExploration.token;

  export const getActiveExplorationResource = (state: AppState) => state.activeExploration._resource;

  export const getExplorationOwnerUserName = (state: AppState) => state.activeExploration.creatorUsername;

  export const getExplorationOwnerName = (state: AppState) => state.activeExploration.creatorName;

  export const getExplorationOwnerAvatar = (state: AppState) => state.activeExploration.creatorAvatar;

  export const getExplorationUpdatedAt = (state: AppState) => state.activeExploration.updatedAt;

  // Viz and pieces of viz selectors
  export const getVizToken = (state: AppState) => state.activeExploration.vizToken;

  export const getSwitchViewToken = (state: AppState) => state.activeExploration.switchViewToken;

  export const getChart = (state: AppState) =>
    fromChartSelectors.createChartSnapshotSelector(FLAMINGO_RESERVED_REFS.Exploration)(state);

  export const getChartResource = (state: AppState) =>
    fromChartSelectors.createChartResourceSelector(FLAMINGO_RESERVED_REFS.Exploration)(state);

  export const getHistory = (state: AppState) =>
    fromChartSelectors.createHistorySelector(FLAMINGO_RESERVED_REFS.Exploration)(state);

  export const getEncoding = (state: AppState) =>
    fromChartSelectors.createEncodingSelector(FLAMINGO_RESERVED_REFS.Exploration)(state);

  export const getTitle = (state: AppState) =>
    fromChartSelectors.createChartTitleSelector(FLAMINGO_RESERVED_REFS.Exploration)(state);

  export const getDescription = (state: AppState) =>
    fromChartSelectors.createChartDescriptionSelector(FLAMINGO_RESERVED_REFS.Exploration)(state);

  export const getCaption = (state: AppState) =>
    fromChartSelectors.createCaptionSelector(FLAMINGO_RESERVED_REFS.Exploration)(state);

  export const getVizType = (state: AppState) =>
    fromChartSelectors.createChartTypeSelector(FLAMINGO_RESERVED_REFS.Exploration)(state);

  export const getQueryToken = (state: AppState) =>
    fromChartSelectors.createQueryTokenSelector(FLAMINGO_RESERVED_REFS.Exploration)(state);

  // @TODO: (@anthonysimone) [GF] now we can get this from the Chart, after the refactor we can remove the switch view token from the exploration
  export const getSwitchViewTokenFromChart = (state: AppState) =>
    fromChartSelectors.createSwitchViewTokenSelector(FLAMINGO_RESERVED_REFS.Exploration)(state);

  export const getFormat = (state: AppState) =>
    fromChartSelectors.createChartFormatSelector(FLAMINGO_RESERVED_REFS.Exploration)(state);

  export const getFieldFormats = (state: AppState) =>
    fromChartSelectors.createChartFieldFormatMapSelector(FLAMINGO_RESERVED_REFS.Exploration)(state);

  export const getFieldColors = (state: AppState) =>
    fromChartSelectors.createChartFieldColorMapSelector(FLAMINGO_RESERVED_REFS.Exploration)(state);

  export const getColorPaletteToken = (state: AppState) =>
    fromChartSelectors.createPaletteSelector(FLAMINGO_RESERVED_REFS.Exploration)(state);

  export const getFacetSizes = (state: AppState) =>
    fromChartSelectors.createChartFacetSizeMapSelector(FLAMINGO_RESERVED_REFS.Exploration)(state);

  export const getIsAutoUpdateDisabled = (state: AppState) =>
    fromChartSelectors.createIsAutoUpdateDisabledSelector(FLAMINGO_RESERVED_REFS.Exploration)(state);
  /**
   * Temporary observables to model flow states that we don't have a better way to handle currently.
   *
   * These are only relevant for the _current_ state of explorations based on using the current
   * SelectedReport architecture and will have to be updated when this changes.
   */

  // Does the query still exist for this exploration? Depends on SelectedReport.
  export const queryExists = createSelector(
    getQueryToken,
    SelectedReportSelectors.getQueries,
    (queryToken, queries) => {
      if (queryToken) {
        return queries.hasRecord(queryToken);
      }
    }
  );

  // This will return the query name associated to the queryToken provided.
  export const queryName = createSelector(getQueryToken, SelectedReportSelectors.getQueries, (queryToken, queries) => {
    if (queryToken && queries.hasRecord(queryToken)) {
      const explorationQuery = queries.getRecord(queryToken);
      return queries.getRecord(queryToken)?.name;
    }
  });

  // Build view_vegas from state
  export const getViewVegas = createSelector(
    getVizType,
    getFormat,
    getEncoding,
    getTitle,
    getDescription,
    getCaption,
    getFieldFormats,
    getFieldColors,
    getFacetSizes,
    getIsAutoUpdateDisabled,
    (
      vizType,
      format,
      encoding,
      vizTitle,
      vizDescription,
      caption,
      fieldFormats,
      fieldColors,
      facetSizes,
      isAutoUpdateDisabled
    ) => ({
      chartType: vizType,
      format,
      encoding,
      title: vizTitle,
      description: vizDescription,
      caption,
      fieldFormats,
      fieldColors,
      facetSizes,
      isAutoUpdateDisabled,
    })
  );

  // Get the data as an exploration
  export const getChartAsExploration = createSelector(
    getQueryToken,
    getColorPaletteToken,
    getViewVegas,
    (queryToken, colorPaletteToken, viewVegas): DataViewResponseTypes.Exploration => ({
      query: queryToken,
      chart: {
        view_vegas: viewVegas,
        view_version: 2,
        color_palette_token: colorPaletteToken,
      },
      name: `Exploration of ${viewVegas.title || 'Untitled'}`,
    })
  );

  export const getEncodedChart = createSelector(
    getVizToken,
    getChart,
    getChartResource,
    getQueryToken,
    getSwitchViewTokenFromChart,
    getHistory,
    (token, chartSnapshot, resource, queryToken, switchViewToken, history): ChartTypes.EncodedChart => ({
      token,
      ...chartSnapshot,
      resource,
      queryToken,
      switchViewToken,
      version: 2,
      v1View: {},
      history,
    })
  );

  export const newExplorationAnalytics = createSelector(getVizType, getVizToken, (vizType, vizToken) => ({
    event_id: generateUUID(),
    product_context: 'report',
    sub_context: 'exploration',
    chart_id: `chart_${vizToken}`,
    chart_type: vizType,
  }));

  //////////
  //
  // GF Selectors below! - The follow selectors are used using the GF exploration pathway.
  //
  /////////

  // Get the active switch view
  export const getGfSwitchView = createSelector(
    getSwitchViewToken,
    (state: AppState) => state,
    (switchViewToken, state) => fromSwitchViewSelectors.getSwitchViewByToken(switchViewToken)(state)
  );

  // Get the active switch view resource
  export const getGfSwitchViewResource = createSelector(
    getSwitchViewToken,
    (state: AppState) => state,
    (switchViewToken, state) => fromSwitchViewSelectors.getSwitchViewFeatureByToken(switchViewToken)(state)
  );

  // Get the active run results
  export const getGfDatasetResultMetadata = createSelector(
    getSwitchViewToken,
    (state: AppState) => state,
    (switchViewToken, state) => fromDatasetResultSelectors.getDatasetResultByToken(switchViewToken)(state)
  );

  export const gfQueryExists = createSelector(getGfSwitchViewResource, (switchView) => switchView?.rootSourceExists);

  export const getGfQueryName = createSelector(getGfDatasetResultMetadata, (datasetResult) => datasetResult?.queryName);

  export const getGfReportDisplayName = (state: AppState) => {
    return state.activeExploration.sourceReportName;
  };

  // @TODO (@anthonysimone) [GF] - Since we need to null check here, this selector should instead be moved
  // to an observable in the active-exploration facade when this feature can be migrated over
  export const getGfQueryRunToken = createSelector(getGfDatasetResultMetadata, (datasetResult) => {
    return datasetResult?.queryRunToken;
  });

  export const getGfRunState = createSelector(
    getGfDatasetResultMetadata,
    (datasetResult) => datasetResult?.queryRunState
  );

  export const getGfQueryRunCompletedAt = createSelector(
    getGfDatasetResultMetadata,
    (datasetResult) => datasetResult?.completedAt
  );

  export const getGfRunRawSql = createSelector(getGfDatasetResultMetadata, (datasetResult) => {
    return datasetResult?.rawQuery;
  });

  export const getGfReportRunFormFields = (state: AppState) => state.activeExploration.reportRunFormFields;

  // Get the chart state!
  export const getGfExplorationChartState = createSelector(
    getEncodedChart,
    fromSwitchViewSelectors.getModel,
    getQueryToken, // currently name is queryToken
    (chart, model, queryToken) => getChartState(chart, model, queryToken)
  );

  export const getGfSpaceName = (state: AppState) => state.activeExploration.spaceName;

  export const getGfSpaceUrl = (state: AppState) => state.activeExploration.spaceUrl;

  export const getGfSpaceDefaultAccess = (state: AppState) => state.activeExploration.defaultAccess;

  export const getGfIsSpaceViewable = (state: AppState) => state.activeExploration.isSpaceViewable;

  export const getGfIsCollectionCommunity = (state: AppState) => state.activeExploration.isCollectionCommunity;

  export const getGfIsCollectionPersonal = (state: AppState) => state.activeExploration.isCollectionPersonal;

  export const getGfDrilldownEnabled = (state: AppState) => state.activeExploration.drilldownEnabled;
}
